// =============================================================================
// ui/tabs/ReviewCoachModal.tsx — Coach Me: this review.
// -----------------------------------------------------------------------------
// The period's numbers, the trader's own reflection, and one question: what is
// the single highest-leverage thing to change next period.
//
// ── 🚨 OPENING THIS MUST NEVER SPEND A RUN ──
//
// Every call is billed and burns a per-item allowance the trader cannot get back
// until tomorrow. So the modal opens on a confirmation that says what it will do,
// and the request happens on a press. An effect firing on mount would bill
// someone for opening a window, and empty their allowance on a double render.
//
// ── ⚠ THE BRIEF INCLUDES THE UNSAVED TYPING ──
//
// It is built by the tab from the textareas as they stand, not from the last
// saved row. A trader who writes a reflection and presses this without saving
// first must not be coached on the empty version of it.
//
// ── ⚠ THE MODEL'S TEXT IS RENDERED AS ELEMENTS, NEVER AS MARKUP ──
//
// See `domain/coachProse`. There is no string here that becomes HTML.
//
// ⚠ THIS IS THE SECOND MODAL OF THIS SHAPE — `CompareAccountsModal` is the
// first, and the confirm/running/result/error machinery is deliberately the
// same. They are not shared yet because unifying them means changing the
// Accounts tab and the eight tests that hold it, which is its own change.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSupabase } from '../state/supabase';
import { runFreeformCoach, COACH_RUNS_PER_ITEM } from '../../io/ai/coachRun';
import { AiCoachError } from '../../io/ai/aiCoach';
// ⚠ `ui/` may not import `domain/` (D47), and the prose renderer lives there.
// It is reached through the selector that already owns that door for the
// Accounts modal rather than through a second re-export of the same function.
import { proseBlocks, type ProseBlock, type ProseSpan } from '../../selectors/accountComparison';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface ReviewCoachModalProps {
  /** The prompt, already built by the tab. Never rebuilt in here. */
  brief: string;
  /** Distinguishes one period's read from another, for the cache and the cap. */
  cacheKey: string;
  /** `Weekly` / `Monthly`, for the heading. */
  cadenceLabel: string;
  periodLabel: string;
  onClose: () => void;
}

interface Stored { runs: number; text: string; ranAt: string }

const KEY = (cacheKey: string) => `tc.coachrun.${cacheKey}`;

function readStored(cacheKey: string): Stored {
  try {
    const raw = localStorage.getItem(KEY(cacheKey));
    if (!raw) return { runs: 0, text: '', ranAt: '' };
    const v = JSON.parse(raw) as Partial<Stored>;
    return {
      runs: Number(v.runs) || 0,
      text: typeof v.text === 'string' ? v.text : '',
      ranAt: typeof v.ranAt === 'string' ? v.ranAt : '',
    };
  } catch {
    // ⚠ A corrupt entry must not take the modal down. Losing the saved read is
    // an inconvenience; a crash on open is a broken feature.
    return { runs: 0, text: '', ranAt: '' };
  }
}

function writeStored(cacheKey: string, v: Stored): void {
  try { localStorage.setItem(KEY(cacheKey), JSON.stringify(v)); } catch { /* full or blocked */ }
}

type View = 'confirm' | 'running' | 'result' | 'error';

export function ReviewCoachModal(
  { brief, cacheKey, cadenceLabel, periodLabel, onClose }: ReviewCoachModalProps,
) {
  const { sb } = useSupabase();
  const [stored, setStored] = useState<Stored>(() => readStored(cacheKey));
  const [view, setView] = useState<View>('confirm');
  const [error, setError] = useState<{ message: string; capped: boolean } | null>(null);

  const runsLeft = Math.max(0, COACH_RUNS_PER_ITEM - stored.runs);

  const run = useCallback(async () => {
    if (!sb) return;
    setView('running');
    setError(null);
    try {
      const res = await runFreeformCoach(sb, brief, cacheKey);
      const next: Stored = {
        runs: stored.runs + 1,
        text: res.text,
        // ⚠ Stamped here rather than in `domain/`, which reads no clock (D26).
        ranAt: new Date().toISOString(),
      };
      writeStored(cacheKey, next);
      setStored(next);
      setView('result');
    } catch (e) {
      const capped = e instanceof AiCoachError && e.code === 'COACH_CAP';
      if (capped && e instanceof AiCoachError && e.reason === 'item_cap') {
        // 🚨 THE SERVER SAYS THE ALLOWANCE IS GONE, SO THE LOCAL COUNT IS WRONG
        // and must be brought up — otherwise the confirmation keeps offering a
        // Run that can only fail. Happens whenever the runs were spent on
        // another device or the browser was cleared.
        const next = { ...stored, runs: COACH_RUNS_PER_ITEM };
        writeStored(cacheKey, next);
        setStored(next);
      }
      // ⚠ LOGGED HERE AND NOWHERE ELSE. `runFreeformCoach` wraps and rethrows
      // rather than reporting, so this catch is the one place the failure exists
      // — and logging it there as well would file every coach failure twice.
      setError({ message: reportFailure('review coach run', e), capped });
      setView('error');
    }
  }, [sb, brief, cacheKey, stored]);

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Coach Me — This Review">
      <div className="modal wide cmp-modal">
        <header className="modal-head">
          <b>AI Coach — {cadenceLabel.toLowerCase()} review</b>
          <button type="button" className="btn" onClick={onClose}>×</button>
        </header>

        <div className="modal-body">
          {view === 'confirm' ? (
            <div className="cmp-confirm">
              <p>
                An <b>AI Coach read</b> of <b>{periodLabel}</b> — your numbers, your
                reflection, and the one change worth making next period.
              </p>
              {/* 🚨 THE SENTENCE THAT STOPS THIS BEING A SURPRISE BILL. */}
              <p>
                It runs <b>only when you ask</b>, so opening this window never spends
                one. You get <b>{COACH_RUNS_PER_ITEM} runs per review</b>.
              </p>
              {/* ⚠ SAYS THAT UNSAVED TYPING COUNTS. Otherwise the safe assumption
                  is that it reads the last save, and the trader saves first "just
                  in case" — or worse, assumes it did and it did not. */}
              <p className="muted">
                Whatever is in the boxes right now is what it reads, saved or not.
              </p>
              <p className="muted">
                {runsLeft === COACH_RUNS_PER_ITEM
                  ? 'Both runs still available for this review.'
                  : runsLeft > 0
                    ? `${runsLeft} of ${COACH_RUNS_PER_ITEM} runs left for this review.`
                    : 'No runs left for this review.'}
                {stored.text ? ' A saved read is available.' : ''}
              </p>
              <div className="cmp-actions">
                {runsLeft > 0 ? (
                  <button type="button" className="btn primary" onClick={() => void run()}>
                    Run the review
                  </button>
                ) : (
                  <span className="muted">Your runs for this review are used up.</span>
                )}
                {stored.text ? (
                  <button type="button" className="btn" onClick={() => setView('result')}>
                    Read the last one
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {view === 'running' ? (
            <p className="muted cmp-busy" role="status">Reading your {periodLabel}…</p>
          ) : null}

          {view === 'result' && stored.text ? (
            <Result
              text={stored.text} ranAt={stored.ranAt} runsLeft={runsLeft}
              onRerun={() => void run()}
            />
          ) : null}

          {view === 'error' && error ? (
            <div className="cmp-error" role="alert">
              <p>{error.message}</p>
              {/* 🚨 SAYS WHETHER IT COST ANYTHING. A failed call is free — the
                  function records a run only on success — and a trader who thinks
                  a network error burned half their allowance will not press the
                  button again. */}
              <p className="muted">
                {error.capped
                  ? 'You can still read your last saved one, or come back tomorrow.'
                  : 'This did not count against your allowance — try again in a moment.'}
              </p>
              <div className="cmp-actions">
                <button type="button" className="btn" onClick={() => setView('confirm')}>Back</button>
                {stored.text ? (
                  <button type="button" className="btn" onClick={() => setView('result')}>
                    Read the last one
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function Result(
  { text, ranAt, runsLeft, onRerun }: {
    text: string; ranAt: string; runsLeft: number; onRerun: () => void;
  },
) {
  const blocks = useMemo(() => proseBlocks(text), [text]);
  const when = ranAt
    ? new Date(ranAt).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })
    : '';
  return (
    <div className="cmp-result">
      <div className="cmp-tag">AI Coach · your review</div>
      <div className="cmp-prose">
        {blocks.map((b, i) => <Block key={i} b={b} />)}
      </div>
      <div className="cmp-foot">
        <span className="muted">
          {when ? `Read ${when} · ` : ''}
          {runsLeft} of {COACH_RUNS_PER_ITEM} run{runsLeft === 1 ? '' : 's'} left
        </span>
        {runsLeft > 0 ? (
          <button type="button" className="btn" onClick={onRerun}>Run it again</button>
        ) : null}
      </div>
    </div>
  );
}

function Spans({ spans }: { spans: readonly ProseSpan[] }) {
  return <>{spans.map((s, i) => (s.bold ? <b key={i}>{s.text}</b> : <span key={i}>{s.text}</span>))}</>;
}

function Block({ b }: { b: ProseBlock }) {
  if (b.kind === 'heading') {
    // ⚠ Always an h4 or lower. The modal's own title is the h-level above, and a
    // model emitting `# Something` must not outrank the window it is in.
    const Tag = (b.level === 1 ? 'h4' : b.level === 2 ? 'h5' : 'h6') as 'h4' | 'h5' | 'h6';
    return <Tag className="cmp-h"><Spans spans={b.spans} /></Tag>;
  }
  if (b.kind === 'list') {
    return (
      <ul className="cmp-list">
        {b.items.map((it, i) => <li key={i}><Spans spans={it} /></li>)}
      </ul>
    );
  }
  return <p className="cmp-p"><Spans spans={b.spans} /></p>;
}
